import { useMemo, useState } from "react";
import { toast } from "@/hooks/use-toast";
import { isAcceptedKeywordFile } from "@/lib/keyword-file";
import {
  useAddGlobalProhibitedKeyword,
  useBulkAddGlobalProhibitedKeywords,
  useBulkDeleteGlobalProhibitedKeywords,
  useDeleteGlobalProhibitedKeyword,
  useGetGlobalProhibitedKeywords,
} from "@/resources/global-prohibited-keyword/global-prohibited-keyword.api";
import { GlobalKeywordsList } from "./GlobalKeywordsList";
import { GlobalKeywordsAddForm } from "./GlobalKeywordsAddForm";
import { ProhibitedKeywordsScopePointer } from "./ProhibitedKeywordsScopePointer";

function parseKeywordList(text: string): string[] {
  return text
    .split(/[,\n]+/)
    .map((k) => k.trim().toLowerCase())
    .filter(Boolean);
}

function notifySearchTermsUpdated(modifiedEntries: number | undefined, plural: boolean) {
  if (!modifiedEntries) return;
  toast({
    title: "Search Terms Updated",
    description: `Removed prohibited keyword${plural ? "s" : ""} from ${modifiedEntries} search term entries.`,
  });
}

function notifyBulkAdded(inserted: number, skipped: number) {
  if (inserted > 0) {
    toast({
      title: "Added",
      description: `${inserted} keyword${inserted !== 1 ? "s" : ""} added${skipped ? `, ${skipped} duplicate${skipped !== 1 ? "s" : ""} skipped` : ""}.`,
    });
  } else {
    toast({ title: "No new keywords", description: "All keywords are already in the list." });
  }
}

// AUT-41 Part 1: Global Settings > Prohibited Keywords. Owns state and mutations;
// GlobalKeywordsList/GlobalKeywordsAddForm/ProhibitedKeywordsScopePointer are the
// three presentational cards this composes.
export function GlobalProhibitedKeywordsTab() {
  const { data: keywords = [], isLoading } = useGetGlobalProhibitedKeywords();

  const addMutation = useAddGlobalProhibitedKeyword();
  const bulkAddMutation = useBulkAddGlobalProhibitedKeywords();
  const deleteMutation = useDeleteGlobalProhibitedKeyword();
  const bulkDeleteMutation = useBulkDeleteGlobalProhibitedKeywords();

  const [newKeyword, setNewKeyword] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [search, setSearch] = useState("");

  const existing = useMemo(
    () => new Set(keywords.map((k) => k.keyword)),
    [keywords],
  );

  const filteredKeywords = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...keywords].sort((a, b) => a.keyword.localeCompare(b.keyword));
    if (!q) return sorted;
    return sorted.filter((k) => k.keyword.includes(q));
  }, [keywords, search]);

  const handleAddSingle = () => {
    const trimmed = newKeyword.trim().toLowerCase();
    if (!trimmed) return;

    if (existing.has(trimmed)) {
      toast({ title: "Duplicate", description: "This keyword is already in the list." });
      return;
    }

    setNewKeyword("");
    addMutation.mutate(
      { keyword: trimmed, origin: "manual" },
      {
        onSuccess: (result) => {
          toast({ title: "Added", description: `"${trimmed}" added to prohibited keywords.` });
          notifySearchTermsUpdated(result.removed_from_search_terms?.modified_entries, false);
        },
      },
    );
  };

  const submitBulk = (parsed: string[]) => {
    if (parsed.length === 0) return;
    bulkAddMutation.mutate(
      { keywords: parsed, origin: "manual" },
      {
        onSuccess: (result) => {
          notifyBulkAdded(result.inserted, result.skipped);
          notifySearchTermsUpdated(result.removed_from_search_terms?.modified_entries, true);
        },
      },
    );
  };

  const handleBulkAdd = () => {
    const parsed = parseKeywordList(bulkText);
    setBulkText("");
    submitBulk(parsed);
  };

  const handleFileUpload = (file: File) => {
    if (!isAcceptedKeywordFile(file)) {
      toast({ title: "Unsupported file type", description: "Please upload a CSV or TXT file." });
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      submitBulk(parseKeywordList(text));
    };
    reader.readAsText(file);
  };

  // AUT-220: the shared list closes its confirm dialog as soon as the request is fired;
  // the delete is optimistic, and the server work behind it (rebuilding search terms,
  // platform-wide for the global tier) can run for minutes.
  const handleRemove = (item: { id: number }, callbacks: { onSuccess: () => void }) => {
    deleteMutation.mutate({ id: item.id }, callbacks);
  };

  // AUT-431: one request for the whole selection; the shared list owns the toast.
  const handleRemoveMany = (ids: number[]) => {
    bulkDeleteMutation.mutate({ ids });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-6 items-start">
      <GlobalKeywordsList
        filteredKeywords={filteredKeywords}
        totalCount={keywords.length}
        isLoading={isLoading}
        search={search}
        onSearchChange={setSearch}
        onRemove={handleRemove}
        onRemoveMany={handleRemoveMany}
        removingId={deleteMutation.isPending ? (deleteMutation.variables?.id ?? null) : null}
      />

      <div className="space-y-6">
        <GlobalKeywordsAddForm
          newKeyword={newKeyword}
          onNewKeywordChange={setNewKeyword}
          onAddSingle={handleAddSingle}
          isAddingSingle={addMutation.isPending}
          bulkText={bulkText}
          onBulkTextChange={setBulkText}
          onBulkAdd={handleBulkAdd}
          isAddingBulk={bulkAddMutation.isPending}
          onFileUpload={handleFileUpload}
        />

        <ProhibitedKeywordsScopePointer />
      </div>
    </div>
  );
}
